import { useState, useEffect, useCallback } from "react";
import { notification } from "antd";
import { macValidation, imeiValidation } from "../../utils/formValidation";
import service from "../../services/Service";
import { withLocale } from "../../l10n";
import Loading from "../../utils/loading";
import withNavigation from '../hooks/withNavigation';
import { useGetModemTypesLookupQuery } from '../../services/lookupApi.js';

const EMPTY_ARRAY = [];

function ModemCard({ lang, navigate, newModem, ident, editable }) {
  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ loading: false });

  const [form, setForm] = useState({
    uuid: "",
    imei: "",
    modem_type_uuid: "",
    serial_number: "",
    mac: "",
    device_password: "",
  });

  const [errors, setErrors] = useState({ imei: "", mac: "" });

  // ===== 2. SELECTORES =====
  const { data: modemTypes = EMPTY_ARRAY } = useGetModemTypesLookupQuery();

  // ===== 3. DERIVADOS =====
  // Los flags requires_* del tipo elegido gobiernan que campos son obligatorios
  const selectedType = modemTypes.find(type => type.uuid === form.modem_type_uuid);
  const requiresMac = selectedType?.requires?.mac ?? false;
  const requiresSerial = selectedType?.requires?.serial ?? false;
  const requiresPassword = selectedType?.requires?.password ?? false;

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateErrors = (field, value) => setErrors(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    if (!ident) return;

    updateUI("loading", true);
    try {
      const res = await service.get("modems/" + ident);
      if (!res || res.data.ok === false) {
        notification["error"]({ message: lang.handle(res?.data?.message) });
        return;
      }
      const data = res.data.data;
      setForm({
        uuid: data.uuid,
        imei: data.imei,
        modem_type_uuid: data.modem_type_uuid,
        serial_number: data.serial_number ?? "",
        mac: data.mac ?? "",
        device_password: data.device_password ?? "",
      });
    } catch (err) {
      console.log("Error devuelto: " + err);
    } finally {
      updateUI("loading", false);
    }
  }, [ident, lang]);

  // ===== 6. HANDLERS =====
  const onInputChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "imei":
        updateErrors("imei", imeiValidation(value) || value === "" ? "" : lang.handle("¡IMEI erróneo!"));
        break;
      case "mac":
        updateErrors("mac", macValidation(value) ? "" : lang.handle("¡MAC errónea!"));
        break;
      default:
        break;
    }
    updateForm(name, value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (errors.imei || errors.mac) {
      notification["error"]({ message: lang.handle("Revisa los campos marcados") });
      return;
    }

    const payload = {
      imei: form.imei,
      modem_type_uuid: form.modem_type_uuid,
      serial_number: form.serial_number || null,
      mac: form.mac || null,
      device_password: form.device_password || null,
    };

    try {
      const res = newModem
        ? await service.post("modems", payload)
        : await service.put("modems/" + form.uuid, payload);

      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
        return;
      }

      notification["success"]({
        message: lang.handle(newModem ? "Modem creado correctamente" : "Modem actualizado correctamente"),
      });
      navigate("/modems/list");
    } catch (err) {
      console.log("Error: " + err);
    }
  };

  // ===== 7. EFFECTS =====
  useEffect(() => {
    if (ident) Pfetch();
  }, [ident, Pfetch]);

  // ===== 8. CONFIG =====
  const title = newModem
    ? lang.handle("Nuevo modem")
    : lang.handle("Información de modem") + " " + form.imei;

  // ===== 9. RENDER =====
  return (
    <div className="card">
      {ui.loading && <Loading loading={ui.loading} />}
      <h4 className="p-3"> {title} </h4>
      <form onSubmit={onSubmit} className="p-3">

        <div className="form-group row">
          <label htmlFor="imei" className="col-sm-2 col-form-label newCard-form-text">IMEI</label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="imei"
              placeholder="860000000000006"
              onChange={onInputChange}
              name="imei"
              value={form.imei}
              disabled={!editable}
              required
            />
            {errors.imei.length > 0 && <span className="boardFormError">{errors.imei}</span>}
          </div>
        </div>

        <div className="form-group row">
          <div className="col newCard-form-text"> {lang.handle("Tipo")} </div>
          <div className="col-sm-10">
            <select
              className="form-control"
              onChange={onInputChange}
              name="modem_type_uuid"
              value={form.modem_type_uuid}
              disabled={!editable}
              required
            >
              <option value=""> -- {lang.handle("Seleccionar opción")} -- </option>
              {modemTypes.map((type) => (
                <option key={type.uuid} value={type.uuid}>{type.code}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group row">
          <label htmlFor="serial_number" className="col-sm-2 col-form-label newCard-form-text">
            {lang.handle("Número de serie")}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="serial_number"
              onChange={onInputChange}
              name="serial_number"
              value={form.serial_number}
              disabled={!editable}
              required={requiresSerial}
            />
          </div>
        </div>

        <div className="form-group row">
          <label htmlFor="mac" className="col-sm-2 col-form-label newCard-form-text">MAC</label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="mac"
              placeholder="30:A4:F5:20:4C:5A"
              onChange={onInputChange}
              name="mac"
              value={form.mac}
              disabled={!editable}
              required={requiresMac}
            />
            {errors.mac.length > 0 && <span className="boardFormError">{errors.mac}</span>}
          </div>
        </div>

        <div className="form-group row">
          <label htmlFor="device_password" className="col-sm-2 col-form-label newCard-form-text">
            {lang.handle("Contraseña del dispositivo")}
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="device_password"
              onChange={onInputChange}
              name="device_password"
              value={form.device_password}
              disabled={!editable}
              required={requiresPassword}
            />
          </div>
        </div>

        {editable && (
          <button id="button-save" className="btn btn-primary d-flex float-right" type="submit">
            <div className="btnText">
              <p> {lang.handle("Guardar")}&nbsp;&nbsp; </p>
              <i className="material-icons"> save </i>
            </div>
          </button>
        )}
      </form>
    </div>
  );
}

export default withLocale(withNavigation(ModemCard));
